// src/pages/app/ExpenseClaimDetailPage.jsx — Detailed view of an expense claim & uploaded receipts
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { expenseClaimsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, XCircle, Banknote, Receipt,
  Calendar, User, DollarSign, FileText, AlertTriangle, Loader2,
  ExternalLink, Building2, Tag, ShieldCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const STATUS_CONFIG = {
  draft: { color: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Draft' },
  pending_approval: { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Pending Approval' },
  manager_approved: { color: 'bg-teal-100 text-teal-800 border-teal-200', label: 'Manager Approved' },
  finance_approved: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Finance Approved' },
  settled: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Settled & Reimbursed' },
  rejected: { color: 'bg-rose-100 text-rose-800 border-rose-200', label: 'Rejected' },
};

const CATEGORY_LABELS = {
  meals: '🍽️ Meals',
  local_transit: '🚕 Local Transit',
  accommodation: '🏨 Accommodation',
  flight: '✈️ Flight',
  train: '🚆 Train',
  fuel: '⛽ Fuel',
  communication: '📱 Communication',
  misc: '📎 Miscellaneous',
};

export default function ExpenseClaimDetailPage() {
  const { id } = useParams();
  const { user, isRole } = useAuth();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [approveModal, setApproveModal] = useState({ open: false, comment: 'Approved' });
  const [rejectModal, setRejectModal] = useState({ open: false, comment: '' });
  const [showSettleModal, setShowSettleModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await expenseClaimsApi.get(id);
      setClaim(data);
    } catch (err) {
      toast.error('Failed to load expense claim');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await expenseClaimsApi.approve(id, { comment: approveModal.comment || 'Approved' });
      toast.success('Expense claim approved!');
      setApproveModal({ open: false, comment: 'Approved' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.comment.trim() || rejectModal.comment.trim().length < 5) {
      toast.error('Rejection comment required (min 5 chars)');
      return;
    }
    setActionLoading(true);
    try {
      await expenseClaimsApi.reject(id, { comment: rejectModal.comment });
      toast.success('Expense claim rejected');
      setRejectModal({ open: false, comment: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rejection failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSettle = async () => {
    setActionLoading(true);
    try {
      await expenseClaimsApi.settle(id, { comment: 'Settled via CTEMS' });
      toast.success('Claim marked as settled!');
      setShowSettleModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Settlement failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getFullUrl = (urlStr) => {
    if (!urlStr) return '';
    if (urlStr.startsWith('http://') || urlStr.startsWith('https://') || urlStr.startsWith('data:')) {
      return urlStr;
    }
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const serverHost = apiBase.replace(/\/api\/?$/, '');
    return `${serverHost}${urlStr.startsWith('/') ? '' : '/'}${urlStr}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500 text-lg">Expense claim not found</p>
        <Link to="/app/expense-claims" className="mt-4 inline-block text-blue-600 font-semibold hover:underline">
          ← Back to Expense Claims
        </Link>
      </div>
    );
  }

  const status = STATUS_CONFIG[claim.status] || STATUS_CONFIG.draft;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/app/expense-claims" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">{claim.title}</h1>
              <span className={cn("px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize", status.color)}>
                {status.label}
              </span>
            </div>
            <p className="text-slate-500 text-xs mt-0.5">
              Claim ID: #{claim.id} • Created on {new Date(claim.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {isRole('manager', 'admin') && claim.status === 'pending_approval' && (
            <>
              <button
                onClick={() => setApproveModal({ open: true, comment: 'Approved' })}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" /> Approve
              </button>
              <button
                onClick={() => setRejectModal({ open: true, comment: '' })}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors"
              >
                <XCircle className="w-4 h-4" /> Reject
              </button>
            </>
          )}

          {isRole('finance', 'admin') && ['manager_approved', 'finance_approved'].includes(claim.status) && (
            <button
              onClick={() => setShowSettleModal(true)}
              disabled={actionLoading}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors"
            >
              <Banknote className="w-4 h-4" /> Settle / Reimburse
            </button>
          )}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Total Expenses</p>
            <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1">₹{parseFloat(claim.total_amount || 0).toLocaleString('en-IN')}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">{claim.items?.length || 0} line items</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Cash Advance Deducted</p>
            <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1">₹{parseFloat(claim.cash_advance_deducted || 0).toLocaleString('en-IN')}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Pre-trip advance</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50/50 border-emerald-200">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Net Reimbursable</p>
            <p className="text-lg sm:text-xl font-bold text-emerald-700 mt-1">₹{parseFloat(claim.net_reimbursable || 0).toLocaleString('en-IN')}</p>
            <p className="text-[11px] text-emerald-600 mt-0.5">Final Payout Amount</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Claimant</p>
            <p className="text-sm font-bold text-slate-900 mt-1 truncate">{claim.employee_name || 'Employee'}</p>
            <p className="text-[11px] text-slate-400 truncate">{claim.employee_email || '—'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Details & Line Items */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {/* Claim Line Items */}
          <Card>
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-sm sm:text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-blue-600" />
                  Expense Items & Uploaded Bills
                </span>
                <span className="text-xs font-normal text-slate-500">
                  {claim.items?.length || 0} Item(s)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-slate-100">
              {claim.items?.map((item, idx) => {
                const itemReceiptUrl = getFullUrl(item.receipt_url);
                const isImage = item.receipt_url && (
                  item.receipt_url.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i) ||
                  item.receipt_url.startsWith('data:image/')
                );
                return (
                  <div key={item.id || idx} className="p-3 sm:p-4 lg:p-5 space-y-3 hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="inline-block text-xs font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-md mb-1.5">
                          {CATEGORY_LABELS[item.category] || item.category}
                        </span>
                        <h4 className="text-sm font-semibold text-slate-900">
                          {item.vendor_name ? item.vendor_name : 'Expense Line Item'}
                        </h4>
                        {item.description && (
                          <p className="text-xs text-slate-600 mt-0.5">{item.description}</p>
                        )}
                        <p className="text-[11px] text-slate-400 mt-1">
                          Date: {new Date(item.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>

                      <div className="text-right shrink-0">
                        <span className="text-base sm:text-lg font-bold text-slate-900 block">
                          ₹{parseFloat(item.amount).toLocaleString('en-IN')}
                        </span>
                        {item.is_flagged && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200 mt-1">
                            <AlertTriangle className="w-3 h-3 text-amber-600" /> Policy Flag
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Uploaded Receipt / Bill File Box */}
                    {item.receipt_url ? (
                      <div className="mt-3 p-3 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 overflow-hidden min-w-0">
                          {isImage ? (
                            <img
                              src={itemReceiptUrl}
                              alt="Receipt thumbnail"
                              className="w-10 h-10 rounded-lg object-cover border border-slate-200 shrink-0 bg-white"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 shrink-0 flex items-center justify-center font-bold text-xs">
                              DOC
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-800 truncate">Uploaded Receipt / Bill</p>
                            <p className="text-[11px] text-slate-500 truncate max-w-[200px]">{item.receipt_url}</p>
                          </div>
                        </div>

                        <a
                          href={itemReceiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-semibold flex items-center gap-1.5 shrink-0 transition-colors shadow-xs"
                        >
                          <ExternalLink className="w-3.5 h-3.5 text-slate-500" /> View / Download Bill
                        </a>
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-400 italic">No receipt file uploaded for this item.</p>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          {/* Trip Connection */}
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Linked Trip Info</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div>
                <p className="text-slate-400 uppercase tracking-wide font-semibold">Trip Destination</p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">{claim.trip_destination || '— Direct Claim —'}</p>
              </div>
              {claim.manager_comment && (
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-slate-400 uppercase tracking-wide font-semibold">Manager Remark</p>
                  <p className="text-slate-700 mt-0.5 bg-slate-50 p-2.5 rounded-lg border border-slate-200">{claim.manager_comment}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audit / Workflow summary */}
          <Card className="bg-slate-50/50 border-slate-200">
            <CardHeader><CardTitle className="text-sm font-semibold flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Claim Audit & Status</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-xs text-slate-600">
              <div className="flex justify-between py-1 border-b border-slate-200">
                <span>Submitted At</span>
                <span className="font-semibold text-slate-800">{claim.submitted_at ? new Date(claim.submitted_at).toLocaleDateString('en-IN') : '—'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-200">
                <span>Approved At</span>
                <span className="font-semibold text-slate-800">{claim.approved_at ? new Date(claim.approved_at).toLocaleDateString('en-IN') : '—'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span>Settled At</span>
                <span className="font-semibold text-slate-800">{claim.settled_at ? new Date(claim.settled_at).toLocaleDateString('en-IN') : '—'}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approve Modal */}
      <Dialog open={approveModal.open} onOpenChange={open => setApproveModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Approve Expense Claim</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Confirm approval for ₹{claim.net_reimbursable} reimbursable amount.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Approval Comment</label>
              <input
                type="text"
                value={approveModal.comment}
                onChange={e => setApproveModal(p => ({ ...p, comment: e.target.value }))}
                placeholder="Optional approval note..."
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => setApproveModal(p => ({ ...p, open: false }))}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={actionLoading}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
            >
              Confirm Approval
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={open => setRejectModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Reject Expense Claim</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              State the reason for rejection (at least 5 characters).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Rejection Reason *</label>
              <textarea
                rows={3}
                value={rejectModal.comment}
                onChange={e => setRejectModal(p => ({ ...p, comment: e.target.value }))}
                placeholder="Reason..."
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => setRejectModal(p => ({ ...p, open: false }))}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={actionLoading || !rejectModal.comment.trim() || rejectModal.comment.trim().length < 5}
              className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50"
            >
              Confirm Rejection
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle Confirmation Modal */}
      <AlertDialog open={showSettleModal} onOpenChange={setShowSettleModal}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-1 mx-auto sm:mx-0">
              <Banknote className="w-6 h-6" />
            </div>
            <AlertDialogTitle className="text-lg sm:text-xl font-bold text-slate-900">
              Settle / Reimburse Expense Claim
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Are you sure you want to mark this expense claim of <strong>₹{parseFloat(claim?.net_reimbursable || 0).toLocaleString('en-IN')}</strong> as settled/reimbursed? The employee will receive an instant notification.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={() => setShowSettleModal(false)}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSettle}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              {actionLoading ? 'Settling...' : 'Confirm Settlement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
